/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/purity */
import { Avatar, Badge, Box, Button, Drawer, Skeleton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import _ from "lodash";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDialogs } from "../../../components";
import { FormProvider, FormState } from "../../../components/FormProvider";
import Config from "../../../config";
import { Child } from "../../../models";
import { useGetAllChildrenFilteredQuery, useGetFamilyByIdQuery, useSaveChildMutation } from "../../../redux/apiStore";
import {
  calculateDateDifference,
  describeDateDifference,
  formatAsShortDate,
  formatNumberAsCurrency,
  getChildImageUrlFromChild,
  parseDate,
} from "../../../utils";
import { AddChildForm } from "../children/detail";

export function ChildrenTable(props: { familyId: number }) {
  const { familyId } = props;
  const getFamilyById = useGetFamilyByIdQuery({ id: familyId });
  const family = getFamilyById?.data;
  const children = useGetAllChildrenFilteredQuery({ familyId });
  const [saveChild, saveChildStatus] = useSaveChildMutation();
  const navigate = useNavigate();
  const theme = useTheme();
  const orderedFilteredData = _.orderBy(children.data, [(x) => x.isActive, (x) => x.firstName], ["desc", "asc"]);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [adding, setAdding] = useState<boolean>(false);
  const formDataGetterRef = useRef<(() => Child | null) | null>(null);
  const resetFormRef = useRef<(() => void) | null>(null);
  const dialogs = useDialogs();

  const getEnrollmentDatesString = (child: Child): string => {
    const enrollmentStart = formatAsShortDate(parseDate(child.enrollmentStartCentralDateTime));
    const enrollmentEnd = formatAsShortDate(parseDate(child.enrollmentEndCentralDateTime));
    if (enrollmentStart.length === 0 && enrollmentEnd.length === 0) {
      return "Unknown";
    }
    return `${enrollmentStart.length === 0 ? "Unknown" : enrollmentStart} - ${enrollmentEnd.length === 0 ? "Present" : enrollmentEnd}`;
  };

  const handleSelectChild = (child: Child) => {
    navigate(`/families/${familyId}/children/${child.id}`);
  };

  const handleAddChild = async (form: FormState) => {
    const child = formDataGetterRef.current ? formDataGetterRef.current() : null;
    form.dismissAlert();
    if (!child) {
      form.notifyError("Failed to save child. Child is null for some reason");
      return;
    }
    try {
      const result = await saveChild(child).unwrap();
      setAdding(false);
      form.notifySuccess("Child saved");
      handleSelectChild(result);
    } catch (err) {
      form.notifyError("Error saving child", err);
    }
  };

  const handleDeleteAddChild = async (form: FormState) => {
    // unneded for adding
  };

  const handleCancelAddChild = async (form: FormState) => {
    try {
      if (resetFormRef.current) {
        resetFormRef.current();
        setAdding(false);
      } else {
        console.error("Failed to undo form changes. No form reset method has been set.");
      }
    } catch (err) {
      form.notifyError("Error undoing changes", err);
    }
  };

  const loadingRow = (
    <TableRow sx={{ cursor: "pointer" }}>
      <TableCell component="th" scope="row">
        <Stack direction="row" columnGap={1} alignItems="center">
          <Badge variant="dot" color="success" sx={{ "& .MuiBadge-dot": { backgroundColor: (theme) => grey[500] } }}>
            <Avatar alt="child picture" src={`${Config.api.uri}/children/fake.jpg?t=${Date.now()}`} sx={{ width: 32, height: 32 }} />
          </Badge>
          <Skeleton width={150} height={30} />
        </Stack>
      </TableCell>
      {!isSmallScreen && (
        <TableCell>
          <Skeleton width={200} height={30} />
        </TableCell>
      )}
      <TableCell>
        <Skeleton width={120} height={30} />
      </TableCell>
      {!isSmallScreen && (
        <TableCell>
          <Skeleton width={120} height={30} />
        </TableCell>
      )}
    </TableRow>
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={adding}
        sx={{ zIndex: 1550 }}
        slotProps={{
          paper: {
            sx: {
              width: isSmallScreen ? "100%" : "400px", // 100% width on mobile
              zIndex: 1550,
            },
          },
        }}
      >
        <Stack sx={{ p: 2, height: "100%" }}>
          <FormProvider fullHeight onSave={handleAddChild} onCancel={handleCancelAddChild} onDelete={handleDeleteAddChild} showControls={false}>
            {getFamilyById?.data && <AddChildForm family={getFamilyById?.data} dataGetterRef={formDataGetterRef} resetFormRef={resetFormRef} />}
          </FormProvider>
        </Stack>
      </Drawer>
      <TableContainer component={Paper}>
        <Stack direction="row" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ m: 0 }}>
            Children
          </Typography>
          <Box flexGrow={1} />
          <Button variant="outlined" onClick={() => setAdding(true)}>
            Add
          </Button>
        </Stack>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              {!isSmallScreen && <TableCell>Age</TableCell>}
              <TableCell>Rate</TableCell>
              {!isSmallScreen && <TableCell>Enrollment</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {children.isFetching && [loadingRow]}
            {orderedFilteredData?.map((child) => {
              const birthDate = parseDate(child.birthDate);
              const age = birthDate ? describeDateDifference(calculateDateDifference(birthDate, new Date())) : null;

              return (
                <TableRow key={child.id} sx={{ cursor: "pointer" }} hover onClick={() => handleSelectChild(child)}>
                  <TableCell component="th" scope="row">
                    <Stack direction="row" columnGap={1} alignItems="center">
                      <Badge
                        variant="dot"
                        color="success"
                        sx={{ "& .MuiBadge-dot": { backgroundColor: (theme) => (child.isActive ? theme.palette.success.light : grey[500]) } }}
                      >
                        <Avatar alt="child picture" src={getChildImageUrlFromChild(child)} sx={{ width: 32, height: 32 }} />
                      </Badge>
                      {child.firstName} {child.lastName}
                    </Stack>
                  </TableCell>
                  {!isSmallScreen && <TableCell>{age}</TableCell>}
                  <TableCell>{formatNumberAsCurrency(child.weeklyRate)} / Week</TableCell>
                  {!isSmallScreen && <TableCell>{getEnrollmentDatesString(child)}</TableCell>}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
